import { PATH_TOP_SPENDER } from "@configs";
import { IGetLeaderboard, IGetLeaderboardList, ILeaderboardOwn } from "@interfaces";
import { useNotify } from "@utils";
import { Col, Divider, Row, Skeleton } from "antd";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router";
import { leaderboardAPI } from "src/API/leaderboard";
import { isUserInactiveFor } from "src/Helpers/useActivities";
import {
    IGetWebsaleTopSpenderExample,
    IGetWebsaleTopSpendersExample,
} from "src/Interfaces/topCharoen";
import { deduplicate } from "src/Utils/requestDeduper";
import styled from "styled-components";
import { BackIcon } from "../icon/TopSpender/BackIcon";
import badgeIcon from "../icon/TopSpender/BadgesRanking.png";

interface IProps {
    userInfo: any;
    loggedInUser?: IGetWebsaleTopSpenderExample;
}

const leaderboardCache = new Map<string, IGetLeaderboard>();
const leaderboardOwnCache = new Map<string, ILeaderboardOwn[]>();

export const ComponentTopSpenderList = (props: IProps) => {
    const PAGE_SIZE = 20;
    const { error } = useNotify();
    const { userInfo, loggedInUser } = props;
    const history = useHistory();
    const [userList, setUserList] = useState<IGetWebsaleTopSpendersExample[]>();
    const { id } = useParams<{ id: string }>();
    const [leaderboard, settLeaderboard] = useState<IGetLeaderboard>();
    const [leaderboardList, settLeaderboardList] = useState<IGetLeaderboardList[]>([]);
    const [leaderboardOwn, settLeaderboardOwn] = useState<ILeaderboardOwn[]>();
    const [page, setPage] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [canViewLeaderboard, setCanViewLeaderboard] = useState<boolean | null>(null);

    const listInnerRef = useRef<HTMLDivElement | null>(null);
    const pageRequestLockRef = useRef(false);
    const isResettingByIdRef = useRef(false);

    useEffect(() => {
        if (!id) return;
        setCanViewLeaderboard(null);
        if (!userInfo?._id) return;
        let isMounted = true;
        const joinedLeaderboardIds = userInfo?.contact?.leaderboardJoin;
        const canViewFromUserInfo =
            Array.isArray(joinedLeaderboardIds) && joinedLeaderboardIds.includes(id);

        if (canViewFromUserInfo) {
            setCanViewLeaderboard(true);
            return () => {
                isMounted = false;
            };
        }

        const verifyAccess = async () => {
            try {
                const getOwn = await deduplicate(`leaderboard-own-${id}-${userInfo?._id}`, () =>
                    leaderboardAPI.getleaderboardOwn(`${id}/${userInfo._id}`)
                );
                if (!isMounted) return;
                const ownData = Array.isArray(getOwn.data) ? getOwn.data : [];
                const ownCacheKey = `${id}-${userInfo._id}`;
                leaderboardOwnCache.set(ownCacheKey, ownData);
                if (ownData.length > 0) {
                    setCanViewLeaderboard(true);
                    return;
                }
                setCanViewLeaderboard(false);
                history.replace(PATH_TOP_SPENDER + "/home/" + id);
            } catch {
                if (!isMounted) return;
                setCanViewLeaderboard(false);
                history.replace(PATH_TOP_SPENDER + "/home/" + id);
            }
        };

        verifyAccess();
        return () => {
            isMounted = false;
        };
    }, [
        history,
        id,
        userInfo?._id,
        Array.isArray(userInfo?.contact?.leaderboardJoin)
            ? userInfo.contact.leaderboardJoin.join(",")
            : "",
    ]);

    const getLeaderboard = async () => {
        if (!id) return;

        const cachedLeaderboard = leaderboardCache.get(id);
        if (cachedLeaderboard) {
            settLeaderboard(cachedLeaderboard);
            return;
        }

        try {
            const get = await deduplicate(`leaderboard-${id}`, () =>
                leaderboardAPI.getleaderboard(id)
            );
            if (get.data) {
                leaderboardCache.set(id, get.data);
            }
            settLeaderboard(get.data);
        } catch (err: any) {
            err.response?.data?.message ? error(err.response?.data?.message) : error(err.message);
        }
    };

    const getLeaderboardOwn = async () => {
        if (!id || !userInfo?._id) return;

        const ownCacheKey = `${id}-${userInfo._id}`;
        const cachedLeaderboardOwn = leaderboardOwnCache.get(ownCacheKey);
        if (cachedLeaderboardOwn) {
            settLeaderboardOwn(cachedLeaderboardOwn);
            return;
        }

        try {
            const getOwn = await deduplicate(`leaderboard-own-${id}-${userInfo?._id}`, () =>
                leaderboardAPI.getleaderboardOwn(`${id}/${userInfo._id}`)
            );
            const ownData = getOwn.data ? getOwn.data : [];
            leaderboardOwnCache.set(ownCacheKey, ownData);
            settLeaderboardOwn(ownData);
        } catch (err: any) {
            err.response?.data?.message ? error(err.response?.data?.message) : error(err.message);
        }
    };

    useEffect(() => {
        if (canViewLeaderboard !== true) return;
        isResettingByIdRef.current = true;
        setPage(1);
        setHasMore(true);
        settLeaderboardList([]);
        pageRequestLockRef.current = false;
    }, [canViewLeaderboard, id]);

    useEffect(() => {
        if (!id || !hasMore || canViewLeaderboard !== true) return;
        if (isResettingByIdRef.current && page !== 1) return;
        if (isResettingByIdRef.current && page === 1) {
            isResettingByIdRef.current = false;
        }
        let isMounted = true;

        const fetchData = async () => {
            try {
                setLoading(true);
                const getList = await deduplicate(`leaderboard-list-${id}-${page}`, () =>
                    leaderboardAPI.getleaderboardList(`${id}?limit=${PAGE_SIZE}&page=${page}`)
                );

                if (!isMounted) return;

                const docs = getList.data?.docs ? getList.data?.docs : [];
                settLeaderboardList((prevData) => (page === 1 ? docs : [...prevData, ...docs]));
                setHasMore(docs.length === PAGE_SIZE);
            } catch (err: any) {
                err.response?.data?.message
                    ? error(err.response?.data?.message)
                    : error(err.message);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
                pageRequestLockRef.current = false;
            }
        };

        fetchData();
        return () => {
            isMounted = false;
        };
    }, [canViewLeaderboard, id, page]);

    useEffect(() => {
        if (canViewLeaderboard !== true) return;
        if (!id) return;
        getLeaderboard();
        const body = document.querySelector("body") as HTMLElement;
        const previousOverflow = body.style.overflow;
        body.style.overflow = "hidden";
        window.scrollTo(0, 0);
        return () => {
            body.style.overflow = previousOverflow;
        };
    }, [canViewLeaderboard, id]);

    useEffect(() => {
        if (canViewLeaderboard !== true) return;
        getLeaderboardOwn();
    }, [canViewLeaderboard, id, userInfo?._id]);

    const onScroll = () => {
        if (listInnerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
            if (
                scrollTop + clientHeight >= scrollHeight - 10 &&
                !loading &&
                hasMore &&
                !pageRequestLockRef.current
            ) {
                pageRequestLockRef.current = true;
                setPage((prevPage) => prevPage + 1);
            }
        }
    };

    const isInactive = isUserInactiveFor(1);

    return leaderboard ? (
        <StyledContainer color={leaderboard?.color ? leaderboard?.color : "#0573cd"}>
            <div className="container">
                <div className="backIcon" onClick={() => history.push("/")}>
                    <BackIcon color={leaderboard?.color ? leaderboard?.color : "#3293DC"} />
                </div>
                <div className="topImage">
                    <img
                        src={leaderboard?.topImage[0] ? leaderboard?.topImage[0] : ""}
                        alt="test"
                        className="topHeaderImage"
                    />
                    <div className="whiteGradiant"></div>
                </div>
                <div className="content">
                    {leaderboard?.rankBy === "UPLOAD_RECEIPT" ? (
                        <h1>ลำดับจำนวนบิล</h1>
                    ) : (
                        <h1>ลำดับยอดสะสม</h1>
                    )}

                    <p>{leaderboard?.detail ? leaderboard?.detail : "Campaign Top Spender"}</p>
                    {/* <small>วันที่ 21 ตุลาคม 2566</small> */}
                    <Divider />
                    <div className="listColumn" onScroll={onScroll} ref={listInnerRef}>
                        <Row>
                            <Col span={6}>
                                <table>
                                    <th>ลำดับที่</th>
                                </table>
                            </Col>
                            <Col span={12}>
                                <table>
                                    <th>ชื่อ-สกุล</th>
                                </table>
                            </Col>
                            <Col span={6}>
                                <table style={{ marginLeft: "auto", textAlign: "right" }}>
                                    <th>
                                        ยอดสะสม{" "}
                                        {leaderboard?.rankBy === "UPLOAD_RECEIPT"
                                            ? "(จำนวนบิล)"
                                            : "(บาท)"}
                                    </th>
                                </table>
                            </Col>
                        </Row>
                        {leaderboardList?.map((user, i) => (
                            <Row
                                className={`listItemsRow`}
                                onClick={() => {
                                    if (
                                        userInfo?.contact &&
                                        user?.userId === userInfo?.contact?.userId
                                    ) {
                                        history.push(PATH_TOP_SPENDER + "/detail/" + id);
                                    }
                                }}
                                key={i}
                            >
                                <Col
                                    span={6}
                                    style={{
                                        paddingLeft: i + 1 <= leaderboard?.badgeRank ? 0 : 12,
                                    }}
                                >
                                    {i + 1 <= leaderboard?.badgeRank ? (
                                        <div
                                            style={{
                                                position: "relative",
                                                width: "24px",
                                            }}
                                        >
                                            {/* <MedalIcon /> */}
                                            <img
                                                src={
                                                    leaderboard?.badgeImage[0]
                                                        ? leaderboard?.badgeImage[0]
                                                        : badgeIcon
                                                }
                                                alt="medal"
                                                style={{
                                                    width: "24px",
                                                }}
                                            />

                                            <div
                                                style={{
                                                    color: "white",
                                                    position: "absolute",
                                                    top: "42%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)",
                                                    fontSize: "10px",
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                {i + 1}
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ color: "black" }}>{i + 1}</div>
                                    )}
                                </Col>
                                <Col span={12}>
                                    <p>
                                        {user?.firstName
                                            ? user?.firstName
                                            : "" + " " + user?.lastName
                                            ? user?.lastName
                                            : "" || "Unknown"}
                                    </p>
                                    <p className="gray">
                                        {user.phoneNumber &&
                                            "0" +
                                                user?.phoneNumber.substring(
                                                    3,
                                                    user?.phoneNumber.length - 7
                                                ) +
                                                "***" +
                                                user?.phoneNumber.substring(
                                                    user?.phoneNumber.length - 4
                                                )}
                                    </p>
                                </Col>
                                <Col span={6}>
                                    <p className="blueText">{user?.total.toLocaleString()}</p>
                                </Col>
                            </Row>
                        ))}
                        <LazyMotion features={domAnimation}>
                            <AnimatePresence>
                                {isInactive && (
                                    <m.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                    >
                                        {leaderboardOwn && leaderboardOwn?.length > 0 ? (
                                            leaderboardOwn?.map((user, i) => (
                                                <Row
                                                    className={`listItemsRow floatingItem`}
                                                    onClick={() => {
                                                        history.push(
                                                            PATH_TOP_SPENDER + "/detail/" + id
                                                        );
                                                    }}
                                                    key={i}
                                                >
                                                    <Col
                                                        span={6}
                                                        style={{
                                                            paddingLeft:
                                                                user.rank >= leaderboard?.badgeRank
                                                                    ? 12
                                                                    : 0,
                                                        }}
                                                    >
                                                        {user.rank <= leaderboard?.badgeRank ? (
                                                            <div
                                                                style={{
                                                                    position: "relative",
                                                                    width: "24px",
                                                                }}
                                                            >
                                                                <img
                                                                    src={
                                                                        leaderboard?.badgeImage[0]
                                                                            ? leaderboard
                                                                                  ?.badgeImage[0]
                                                                            : badgeIcon
                                                                    }
                                                                    alt="medal"
                                                                    style={{
                                                                        width: "24px",
                                                                    }}
                                                                />
                                                                <div
                                                                    style={{
                                                                        color: "white",
                                                                        position: "absolute",
                                                                        top: "43%",
                                                                        left: "50%",
                                                                        transform:
                                                                            "translate(-50%, -50%)",
                                                                        fontSize: "10px",
                                                                        fontWeight: "bold",
                                                                    }}
                                                                >
                                                                    {user.rank} sssss
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            user.rank
                                                        )}
                                                    </Col>
                                                    <Col span={12}>
                                                        <p>
                                                            {user.firstName + " " + user.lastName ||
                                                                "Unknown"}
                                                        </p>
                                                        <p className="gray">
                                                            {user.phoneNumber &&
                                                                "0" +
                                                                    user.phoneNumber.substring(
                                                                        3,
                                                                        user.phoneNumber.length - 7
                                                                    ) +
                                                                    "***" +
                                                                    user.phoneNumber.substring(
                                                                        user.phoneNumber.length - 4
                                                                    )}
                                                        </p>
                                                    </Col>
                                                    <Col span={6}>
                                                        <p className="blueText">
                                                            {user.total.toLocaleString()}
                                                        </p>
                                                    </Col>
                                                </Row>
                                            ))
                                        ) : (
                                            <Row
                                                className={`listItemsRow floatingItem`}
                                                onClick={() => {
                                                    history.push(
                                                        PATH_TOP_SPENDER + "/detail/" + id
                                                    );
                                                }}
                                                key={"1"}
                                            >
                                                <Col
                                                    span={6}
                                                    style={{
                                                        paddingLeft: 12,
                                                    }}
                                                >
                                                    {leaderboardList
                                                        ? leaderboardList.length + 1
                                                        : ""}
                                                </Col>
                                                <Col span={12}>
                                                    <p>
                                                        {userInfo?.contact?.firstName +
                                                            " " +
                                                            userInfo?.contact?.lastName ||
                                                            "Unknown"}
                                                    </p>
                                                    <p className="gray">
                                                        {userInfo?.contact?.phoneNumber &&
                                                            "0" +
                                                                userInfo?.contact?.phoneNumber.substring(
                                                                    3,
                                                                    userInfo?.contact?.phoneNumber
                                                                        .length - 7
                                                                ) +
                                                                "***" +
                                                                userInfo?.contact?.phoneNumber.substring(
                                                                    userInfo?.contact?.phoneNumber
                                                                        .length - 4
                                                                )}
                                                    </p>
                                                </Col>
                                                <Col span={6}>
                                                    <p className="blueText">{"0"}</p>
                                                </Col>
                                            </Row>
                                        )}
                                    </m.div>
                                )}
                            </AnimatePresence>
                        </LazyMotion>
                        {loggedInUser?.data &&
                            userList?.some(
                                (x) => x._id.profile_id !== loggedInUser.data._id.profile_id
                            ) && (
                                <Row
                                    className={`listItemsRow floatingItem`}
                                    onClick={() => {
                                        history.push(
                                            PATH_TOP_SPENDER +
                                                "/list/" +
                                                loggedInUser?.data._id.phone_no.substring(1)
                                        );
                                    }}
                                >
                                    <Col span={6} style={{ paddingLeft: 0 }}>
                                        {loggedInUser.range_no === -1 ? "-" : loggedInUser.range_no}
                                    </Col>
                                    <Col span={12}>
                                        <p>{loggedInUser.data._id.profile_name || "Unknown"}</p>
                                        <p className="gray">
                                            {"0" +
                                                loggedInUser.data._id.phone_no.substring(
                                                    3,
                                                    loggedInUser.data._id.phone_no.length - 7
                                                ) +
                                                "***" +
                                                loggedInUser.data._id.phone_no.substring(
                                                    loggedInUser.data._id.phone_no.length - 4
                                                )}
                                        </p>
                                    </Col>
                                    <Col span={6}>
                                        <p className="blueText">
                                            {loggedInUser.data.sum_sale_value.toLocaleString()}
                                        </p>
                                    </Col>
                                </Row>
                            )}
                    </div>
                </div>
            </div>
        </StyledContainer>
    ) : (
        <Skeleton />
    );
};
const StyledContainer = styled.div<{ color: string }>`
    height: 100dvh;
    .container {
        .backIcon {
            position: absolute;
            top: 20px;
            left: 20px;
            cursor: pointer;
            z-index: 1;
        }
        background: linear-gradient(
                180deg,
                rgba(255, 255, 255, 255) 45%,
                ${(props) => props.color} 100%
            ),
            linear-gradient(
                180deg,
                rgba(195, 155, 108, 0) 0%,
                rgba(195, 155, 108, 0.42) 70.31%,
                #fff 100%
            ),
            #fff;
        // min-height: 100vh;
        .ant-divider {
            background: #e1e1e1;
            width: 90%;
            min-width: 90%;
            margin: auto;
            margin: 22px auto 16px auto;
        }
    }
    .topHeaderImage {
        width: 100%;
    }
    .topHeaderImage {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
    .topImage {
        position: relative;
        min-height: 200px;
        /* padding-bottom: 10px; */
    }
    .whiteGradiant {
        background: linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.62) 90%,
            #fff 100%
        );
        height: 100%;
        width: 100%;
        position: absolute;
        top: 0;
        z-index: 0;
    }
    .content {
        padding: 20px;
        h1 {
            color: ${(p) => p.color || "#3293dc"};
            text-align: center;
            text-shadow: 0px 4px 20px rgba(5, 115, 205, 0.2);
            font-size: 32px;
            font-style: normal;
            font-weight: 700;
            line-height: inherit;
        }
        p {
            color: #525257;
            text-align: center;
            font-size: 16px;
            font-style: normal;
            font-weight: 700;
        }
        small {
            color: #525257;
            text-align: center;
            font-size: 16px;
            font-style: normal;
            font-weight: 700;
            display: block;
        }
        .listColumn {
            display: flex;
            flex-direction: column;
            gap: 6px;
            position: relative;
            overflow: auto;
            height: 62vh;
            padding-bottom: 20px;
            th {
                color: #000;
                font-size: 14px;
                font-style: normal;
                font-weight: 700;
                line-height: inherit;
            }
            .ant-row {
                padding: 6px 18px;
            }
            .listItemsRow {
                padding: 12px 20px;
                border-radius: 12px;
                background: #fff;
                display: flex;
                place-items: center;
                box-shadow: none;
                p {
                    color: #000;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 20px;
                    text-align: left;
                    margin: 0;
                }
                .gray {
                    color: #9b9b9b;
                    font-size: 12px;
                }
                .blueText {
                    color: ${(p) => p.color};
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 700;
                    line-height: inherit;
                    text-align: right;
                }
                .placeIndex {
                    color: #000;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 20px;
                    padding-left: 8px;
                }
                .textrank {
                    color: #fff;
                    font-size: 8px;
                }
            }
            .floatingItem {
                position: fixed;
                right: 20px;
                left: 20px;
                bottom: 80px;
                display: flex;
                padding: 12px 20px;
                border-radius: 12px;
                background: #fff;
                place-items: center;
                box-shadow: 0px 4px 20px 0px rgba(61, 80, 150, 0.55);
                z-index: 1;
                p {
                    color: #000;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 20px;
                    text-align: left;
                    margin: 0;
                }
                .gray {
                    color: #9b9b9b;
                    font-size: 12px;
                }
                .blueText {
                    color: ${(p) => p.color};
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 700;
                    line-height: inherit;
                    text-align: right;
                }
                .placeIndex {
                    color: #000;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 20px;
                    padding-left: 8px;
                }
            }
        }
    }
`;
